import { useState, useEffect, type FormEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { V2Button, StatePanel, LoadingState, Icon } from '@sakhtar/design-system/react';
import logoUrl from '@sakhtar/design-system/assets/logo/sakhtar-white-logo.svg';
import { login, loginDemo } from '../../lib/auth.js';
import { ApiError } from '../../lib/apiClient.js';
import { checkSlugAvailability } from '../../lib/publicApi.js';
import { tenantBasePath } from '../../lib/tenant.js';
import {
  IRAN_MOBILE_PATTERN,
  IRAN_MOBILE_VALIDATION_MESSAGE,
  isValidIranMobileNumber,
} from '../../lib/phoneNumber.js';
import { AUTH_BRAND_COPY, formatOrganizationDisplayLabel } from './authBrand.js';
import { AuthHeroPanel, AuthMobileHeader, useIsDesktopAuth } from './AuthHeroPanel.js';

/** NEW-15 — generic; does not reveal other accounts' state. */
export const MEMBERSHIP_DISABLED_MESSAGE = 'حساب شما توسط مدیر سازمان غیرفعال شده است';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Login.dc.html, tenant credential screen
 *   (`/t/:slug/login`) — desktop (1440×900 split hero/form) and mobile
 *   (390×844 stacked) layouts read directly via DesignSync this session.
 * status: PARTIAL_REAL
 * reconciliation:
 *   - Demo tenants (`demo`/`demo-preview`) render a distinct one-click card
 *     instead of the credential form — the design has no demo-tenant
 *     frame; this is this app's own real behavior (see `loginDemo`), reusing
 *     the same hero-panel/mobile-header chrome so the shell stays identical.
 *   - "مرا به خاطر بسپار" (remember me) has no real backend and is not
 *     rendered — AGENTS.md §1, no UI implying a capability that doesn't
 *     exist yet.
 */
export function LoginPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktopAuth();
  // Prefilled when coming back from password recovery.
  const [loginIdentifier, setLoginIdentifier] = useState(
    (location.state as { mobileNumber?: string } | null)?.mobileNumber ?? '',
  );
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const initialDisabledMessage = (() => {
    const state = location.state as { membershipDisabled?: boolean } | null;
    if (state?.membershipDisabled) return MEMBERSHIP_DISABLED_MESSAGE;
    try {
      if (sessionStorage.getItem('sakhtar.membershipDisabled') === '1') {
        sessionStorage.removeItem('sakhtar.membershipDisabled');
        return MEMBERSHIP_DISABLED_MESSAGE;
      }
    } catch {
      return null;
    }
    return null;
  })();
  const [error, setError] = useState<string | null>(initialDisabledMessage);

  if (!slug) return null;

  const isDemo = slug === 'demo' || slug === 'demo-preview';
  const [checkingTenant, setCheckingTenant] = useState(!isDemo);
  const [tenantNotFound, setTenantNotFound] = useState(false);

  useEffect(() => {
    if (isDemo || !slug) return;
    let active = true;
    checkSlugAvailability(slug)
      .then((res) => {
        if (!active) return;
        if (res.available) {
          setTenantNotFound(true);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setCheckingTenant(false);
      });
    return () => {
      active = false;
    };
  }, [slug, isDemo]);

  useEffect(() => {
    if (!isDemo) return;
    let active = true;
    setSubmitting(true);
    loginDemo(slug)
      .then((result) => {
        if (!active) return;
        navigate(result.redirectTo || `/t/${slug}/admin`, { replace: true });
      })
      .catch(() => {
        if (!active) return;
        setSubmitting(false);
        setError('ارتباط با سرور برای ورود به محیط دمو برقرار نشد. لطفاً دوباره تلاش فرمایید.');
      });
    return () => {
      active = false;
    };
  }, [isDemo, slug, navigate]);

  function handleDemoRetry() {
    setSubmitting(true);
    setError(null);
    loginDemo(slug!)
      .then((result) => {
        navigate(result.redirectTo || `/t/${slug}/admin`, { replace: true });
      })
      .catch(() => {
        setSubmitting(false);
        setError('ارتباط با سرور برقرار نشد. لطفاً کمی بعد دوباره امتحان کنید.');
      });
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const identifier = loginIdentifier.trim();
    if (!isValidIranMobileNumber(identifier)) {
      setError(IRAN_MOBILE_VALIDATION_MESSAGE);
      return;
    }
    setSubmitting(true);
    try {
      const result = await login(slug!, identifier, password);
      if (result.status === 'mfa_required') {
        navigate(`${tenantBasePath(slug!)}/login/otp`, {
          state: {
            preSessionId: result.preSessionId,
            challengeId: result.challengeId,
            mobileNumber: identifier,
          },
        });
        return;
      }
      navigate(result.redirectTo);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        setError('شماره همراه یا رمز عبور را کامل وارد کنید.');
      } else if (err instanceof ApiError && err.code === 'NOT_FOUND') {
        // ADR-001 §2.3 — do not distinguish "no such account" from "wrong password".
        setError('شماره همراه یا رمز عبور نادرست است.');
      } else if (err instanceof ApiError && err.code === 'MEMBERSHIP_DISABLED') {
        setError(MEMBERSHIP_DISABLED_MESSAGE);
      } else if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setError('تعداد تلاش‌ها زیاد بوده — کمی بعد دوباره امتحان کنید.');
      } else {
        setError('مشکلی در ارتباط با سرور پیش آمد. دوباره تلاش کنید.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (checkingTenant) {
    return <LoadingState />;
  }

  if (tenantNotFound) {
    return (
      <div
        style={{
          height: '100dvh',
          display: 'grid',
          placeContent: 'center',
          background: 'var(--sk-bg-canvas)',
        }}
      >
        <StatePanel
          variant="not_found"
          title="فضای کاری یافت نشد"
          description={`فضای کاری با آدرس «${slug}» وجود ندارد.`}
          action={
            <div style={{ display: 'flex', gap: 8 }}>
              <V2Button variant="primary" onClick={() => navigate('/start/login')}>
                ورود به فضای کاری دیگر
              </V2Button>
              <V2Button variant="secondary" onClick={() => navigate('/start/trial')}>
                ساخت فضای کاری جدید
              </V2Button>
            </div>
          }
        />
      </div>
    );
  }

  const errorBox = error ? (
    <div
      role="alert"
      style={{
        display: 'flex',
        gap: 11,
        alignItems: 'flex-start',
        padding: '14px 16px',
        borderRadius: 'var(--sk-r-md)',
        background: 'var(--sk-danger-soft)',
        border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
      }}
    >
      <span style={{ color: 'var(--sk-danger)', display: 'flex', paddingTop: 2, flex: '0 0 auto' }}>
        <Icon name="warn" size="sm" />
      </span>
      <span style={{ fontSize: 12, lineHeight: 1.9, color: 'var(--sk-fg)', flex: 1 }}>{error}</span>
    </div>
  ) : null;

  if (isDemo) {
    const demoCard = (
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: isDesktop ? 20 : 17, fontWeight: 700, color: 'var(--sk-fg)' }}>
            محیط دمو ساختار سیستم
          </span>
          <span style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
            دسترسی کامل مدیر سیستم — بدون نیاز به نام کاربری و رمز عبور
          </span>
        </div>
        <div
          style={{
            padding: 16,
            borderRadius: 'var(--sk-r-md)',
            background: 'var(--sk-bg-elev)',
            border: '1px solid var(--sk-border)',
            textAlign: 'center',
            lineHeight: 1.6,
          }}
        >
          <p style={{ margin: 0, fontWeight: 500, color: 'var(--sk-fg)' }}>
            {submitting
              ? 'در حال ورود خودکار به عنوان مدیر ارشد دمو...'
              : 'فضای دمو با داده‌های نمونه فارسی آماده ورود است.'}
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
            تمامی ماژول‌ها و دسترسی‌های مدیریتی فعال می‌باشند.
          </p>
        </div>
        {errorBox}
        <V2Button
          type="button"
          variant="primary"
          disabled={submitting}
          onClick={handleDemoRetry}
          style={{ width: '100%' }}
        >
          {submitting ? 'در حال اتصال به دمو…' : 'ورود مستقیم به محیط دمو (دسترسی کامل)'}
        </V2Button>
      </div>
    );

    if (!isDesktop) {
      return (
        <div
          className="sk-app sk-auth-mobile"
          dir="rtl"
          style={{
            width: '100%',
            minHeight: '100dvh',
            background: 'var(--sk-bg-canvas)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 26,
            padding: '0 24px',
          }}
        >
          <AuthMobileHeader />
          {demoCard}
        </div>
      );
    }
    return (
      <div
        className="sk-app"
        dir="rtl"
        style={{ width: '100vw', height: '100dvh', background: 'var(--sk-bg-canvas)' }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            direction: 'ltr',
          }}
        >
          <AuthHeroPanel />
          <div
            style={{
              direction: 'rtl',
              display: 'grid',
              placeContent: 'center',
              background: 'var(--sk-bg-canvas)',
              padding: '0 40px',
            }}
          >
            <div style={{ width: 392 }}>{demoCard}</div>
          </div>
        </div>
      </div>
    );
  }

  const tenantLabel = formatOrganizationDisplayLabel(slug) ?? AUTH_BRAND_COPY.brandLabel;
  const avatarInitial = tenantLabel.trim().charAt(0) || 'س';

  const orgIdentity = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: isDesktop ? 14 : 11,
      }}
    >
      <div
        style={{
          width: isDesktop ? 72 : 58,
          height: isDesktop ? 72 : 58,
          borderRadius: isDesktop ? 22 : 18,
          background: 'linear-gradient(158deg, #ffffff 8%, #dcdffb 100%)',
          color: '#4a52c4',
          display: 'grid',
          placeContent: 'center',
          boxShadow:
            '0 12px 24px -10px rgba(76,83,196,.36), inset 0 1.5px 0 rgba(255,255,255,.95), inset 0 -3px 6px rgba(15,18,25,.05)',
          border: '1px solid rgba(255,255,255,.7)',
          fontSize: isDesktop ? 26 : 21,
          fontWeight: 700,
        }}
      >
        {avatarInitial}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
        <span style={{ fontSize: isDesktop ? 20 : 17, fontWeight: 700, color: 'var(--sk-fg)' }}>
          {tenantLabel}
        </span>
        {isDesktop ? (
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 12,
              color: 'var(--sk-fg-subtle)',
              direction: 'ltr',
            }}
          >
            cloud.sakhtar.net/t/{slug}
          </span>
        ) : null}
      </div>
    </div>
  );

  const form = (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 18 }}
    >
      {errorBox}

      <div className="sk-field">
        <label>شماره موبایل</label>
        <div className="sk-input-group">
          {isDesktop ? (
            <span style={{ color: 'var(--sk-fg-subtle)', display: 'flex' }}>
              <Icon name="phone" size="sm" />
            </span>
          ) : null}
          <input
            id="login-mobile"
            name="username"
            type="tel"
            inputMode="numeric"
            autoComplete="username"
            dir="ltr"
            pattern={IRAN_MOBILE_PATTERN}
            maxLength={11}
            placeholder="09123456789"
            value={loginIdentifier}
            onChange={(event) => setLoginIdentifier(event.target.value)}
            style={{ direction: 'ltr', textAlign: 'left', fontWeight: 600, letterSpacing: '.03em' }}
            required
          />
        </div>
      </div>

      <div className="sk-field">
        <label>رمز عبور</label>
        <div className="sk-input-group">
          {isDesktop ? (
            <span style={{ color: 'var(--sk-fg-subtle)', display: 'flex' }}>
              <Icon name="lock" size="sm" />
            </span>
          ) : null}
          <input
            id="login-password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            style={{ direction: 'ltr', textAlign: 'left' }}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
            style={{
              border: 0,
              background: 'transparent',
              padding: 0,
              display: 'flex',
              color: 'var(--sk-fg-subtle)',
              cursor: 'pointer',
            }}
          >
            <Icon name={showPassword ? 'eyeOff' : 'eye'} size="sm" />
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', marginTop: -8 }}>
        <button
          type="button"
          onClick={() =>
            navigate(`${tenantBasePath(slug!)}/login/recovery`, {
              state: { mobileNumber: loginIdentifier.trim() },
            })
          }
          style={{
            border: 0,
            background: 'transparent',
            padding: 0,
            font: 'inherit',
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--sk-brand-600, var(--sk-brand-500))',
            cursor: 'pointer',
          }}
        >
          رمز عبور را فراموش کرده‌اید؟
        </button>
      </div>

      <V2Button type="submit" variant="primary" disabled={submitting} style={{ width: '100%' }}>
        {submitting ? 'در حال ورود…' : 'ورود'}
      </V2Button>
    </form>
  );

  const backLink = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <V2Button variant="ghost" size="sm" onClick={() => navigate('/start/login')}>
        ورود به سازمان دیگر
      </V2Button>
      <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>قدرت گرفته از ساختار سیستم</span>
    </div>
  );

  if (!isDesktop) {
    return (
      <div
        className="sk-app sk-auth-mobile"
        dir="rtl"
        style={{
          width: '100%',
          minHeight: '100dvh',
          background: 'var(--sk-bg-canvas)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            flex: '0 0 auto',
            position: 'relative',
            overflow: 'hidden',
            background: 'var(--sk-brand-800, #23277a)',
            padding: '52px 24px 30px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: -160,
              insetInlineEnd: -120,
              width: 420,
              height: 420,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(120,170,255,.24), rgba(120,170,255,0) 66%)',
              pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={logoUrl} alt="" style={{ height: 32, width: 'auto', display: 'block' }} />
            <span style={{ fontSize: 19, fontWeight: 700, color: '#ffffff' }}>ساختار سیستم</span>
          </div>
          <span style={{ position: 'relative', fontSize: 13, color: 'rgba(255,255,255,.72)' }}>
            محیط ابری یکپارچه
          </span>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 24,
            padding: '0 24px',
          }}
        >
          {orgIdentity}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {form}
            <V2Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/start/login')}
              style={{ width: '100%' }}
            >
              ورود به سازمان دیگر
            </V2Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="sk-app"
      dir="rtl"
      style={{ width: '100vw', height: '100dvh', background: 'var(--sk-bg-canvas)' }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          direction: 'ltr',
        }}
      >
        <AuthHeroPanel />
        <div
          style={{
            direction: 'rtl',
            display: 'grid',
            placeContent: 'center',
            background: 'var(--sk-bg-canvas)',
            padding: '0 40px',
          }}
        >
          <div
            style={{
              width: 392,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 30,
            }}
          >
            {orgIdentity}
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 18 }}>
              {form}
            </div>
            {backLink}
          </div>
        </div>
      </div>
    </div>
  );
}
